import { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { usePageExists } from '@/hooks/useCms';
import FeedbackModal from '@/components/FeedbackModal';

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggle}
      className={`relative flex h-6 w-11 flex-shrink-0 items-center rounded-full px-0.5 transition-colors duration-200 focus:outline-none ${
        isDark ? 'bg-gray-600' : 'bg-gray-300'
      }`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className={`flex h-5 w-5 items-center justify-center rounded-full shadow text-xs ${
          isDark ? 'ml-auto' : ''
        }`}
        style={{ backgroundColor: 'white' }}
      >
        {isDark ? '🌙' : '☀️'}
      </motion.span>
    </button>
  );
}

function BoringPopup() {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(
    () => searchParams.get('boring') === 'true' && !sessionStorage.getItem('boring-dismissed'),
  );

  function dismiss() {
    sessionStorage.setItem('boring-dismissed', '1');
    setVisible(false);
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center"
            initial={{ scale: 0.7, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.85, y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              className="text-5xl mb-4"
              animate={{ rotate: [0, -10, 10, -8, 8, 0] }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              💀
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Really?</h2>
            <p className="text-gray-600 mb-2 text-base leading-relaxed">
              You saw a link on someone's Instagram and thought <em>"yeah, let me click that."</em>
            </p>
            <p className="text-gray-600 mb-2 text-base leading-relaxed">
              This is a private site. There's nothing here for you. You've dedicated a portion of your
              finite life on Earth to loading a page you were never meant to see.
            </p>
            <p className="text-gray-500 mb-6 text-sm">
              Genuinely, get a hobby. Touch grass. Call your mum. Anything.
            </p>
            <motion.button
              onClick={dismiss}
              className="bg-gray-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors w-full"
              whileTap={{ scale: 0.97 }}
            >
              I know. I have no life. Let me in.
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Teasing nav link components ─────────────────────────────────────────────

function BetsNavLink({ isActive }: { isActive: boolean }) {
  return (
    <div style={{ perspective: 500 }} className="relative">
      <motion.div
        animate={{ rotate: [0, -7, 7, -5, 5, 0] }}
        transition={{ repeat: Infinity, repeatDelay: 4, duration: 0.55, ease: 'easeInOut' }}
        whileHover={{
          rotateX: -20,
          rotateY: 12,
          scale: 1.2,
          transition: { type: 'spring', stiffness: 400, damping: 13 },
        }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <Link
          to="/pool"
          className={`relative flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold overflow-hidden transition-all ${
            isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 hover:shadow-[0_4px_14px_rgba(0,0,0,0.10)]'
          }`}
        >
          {/* Pulsing LIVE dot */}
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gray-700" />
          </span>
          Bets
          {/* White shimmer sweep */}
          <motion.span
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 pointer-events-none"
            initial={{ x: '-150%' }}
            animate={{ x: '250%' }}
            transition={{ repeat: Infinity, repeatDelay: 5.5, duration: 0.8, ease: 'easeOut', delay: 3 }}
          />
          {/* 3D depth shadow layer */}
          <motion.span
            className="absolute inset-0 rounded-md bg-black/5 -z-10"
            style={{ transform: 'translateZ(-8px)' }}
          />
        </Link>
      </motion.div>
    </div>
  );
}

function BlogNavLink({ to, isActive }: { to: string; isActive: boolean }) {
  return (
    <div style={{ perspective: 500 }} className="relative">
      <motion.div
        animate={{ y: [0, -3, 0, -2, 0] }}
        transition={{ repeat: Infinity, repeatDelay: 3.5, duration: 1.4, ease: 'easeInOut' }}
        whileHover={{
          rotateX: -20,
          rotateY: -12,
          scale: 1.2,
          transition: { type: 'spring', stiffness: 400, damping: 13 },
        }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <Link
          to={to}
          className={`relative flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold overflow-hidden transition-all ${
            isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 hover:shadow-[0_4px_14px_rgba(0,0,0,0.10)]'
          }`}
        >
          {/* Rotating sparkle */}
          <motion.span
            className="text-gray-400 text-xs select-none leading-none"
            animate={{ rotate: 360, scale: [1, 1.4, 1] }}
            transition={{
              rotate: { duration: 9, repeat: Infinity, ease: 'linear' },
              scale: { duration: 2.2, repeat: Infinity, ease: 'easeInOut' },
            }}
          >
            ✦
          </motion.span>
          Blog
          {/* White shimmer sweep */}
          <motion.span
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 pointer-events-none"
            initial={{ x: '-150%' }}
            animate={{ x: '250%' }}
            transition={{ repeat: Infinity, repeatDelay: 4, duration: 0.75, ease: 'easeInOut' }}
          />
          {/* 3D depth layer */}
          <motion.span
            className="absolute inset-0 rounded-md bg-black/5 -z-10"
            style={{ transform: 'translateZ(-8px)' }}
          />
        </Link>
      </motion.div>
    </div>
  );
}

// ─── Sign-in teaser bubble ────────────────────────────────────────────────────

const TEASER_TEXTS = [
  'Join the fun — sign in',
  'Log in to place your bets',
  'Sign in to participate',
  'Log in for more features',
  'Sign in to track your picks',
  'Your pick is one tap away',
];

function randomTeaserText() {
  return TEASER_TEXTS[Math.floor(Math.random() * TEASER_TEXTS.length)];
}

// Shared bubble — set `arrowRight` to true if the caret should point right
function TeaserBubble({ arrowRight = false }: { arrowRight?: boolean }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const teaserText = useMemo(randomTeaserText, []);

  // Bypass the CSS-override dark mode system in index.css with inline styles
  const bg    = isDark ? 'rgb(249 250 251)'  : 'rgb(15 23 42)';   // gray-50  / slate-900
  const color = isDark ? 'rgb(15 23 42)'     : 'rgb(249 250 251)';
  const glow  = isDark
    ? '0 0 0 1px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.14)'
    : '0 0 0 1px rgba(255,255,255,0.06), 0 8px 28px rgba(0,0,0,0.55)';
  const shimmerColor = isDark
    ? 'rgba(15,23,42,0.25)'
    : 'rgba(255,255,255,0.18)';

  return (
    <div className="absolute right-0 top-full mt-3 z-50 select-none pointer-events-none">
      {/* Arrow caret */}
      <div
        className={`absolute -top-1.5 w-3 h-3 rotate-45 rounded-sm ${arrowRight ? 'right-3' : 'right-5'}`}
        style={{ backgroundColor: bg }}
      />

      {/* Main card */}
      <motion.div
        className="relative overflow-hidden rounded-xl px-4 py-2.5 whitespace-nowrap"
        style={{ backgroundColor: bg, color, boxShadow: glow, minWidth: 190 }}
        initial={{ opacity: 0, scale: 0.6, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 420, damping: 22, delay: 0.15 }}
      >
        {/* Floating idle */}
        <motion.div
          className="flex items-center gap-2"
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
        >
          {/* Pulsing dot */}
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <motion.span
              className="absolute inline-flex h-full w-full rounded-full"
              style={{ backgroundColor: isDark ? 'rgb(99 102 241)' : 'rgb(129 140 248)' }}
              animate={{ scale: [1, 1.9, 1], opacity: [0.7, 0, 0.7] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
            />
            <span
              className="relative inline-flex h-2 w-2 rounded-full"
              style={{ backgroundColor: isDark ? 'rgb(99 102 241)' : 'rgb(165 180 252)' }}
            />
          </span>

          <span className="text-sm font-semibold tracking-tight">{teaserText}</span>

          {/* Arrow →  */}
          <motion.span
            className="ml-1 text-sm font-bold"
            style={{ opacity: 0.6 }}
            animate={{ x: [0, 3, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          >
            →
          </motion.span>
        </motion.div>

        {/* Shimmer sweep */}
        <motion.span
          className="pointer-events-none absolute inset-0 -skew-x-12"
          style={{
            background: `linear-gradient(90deg, transparent, ${shimmerColor}, transparent)`,
          }}
          initial={{ x: '-150%' }}
          animate={{ x: '250%' }}
          transition={{ duration: 1.1, repeat: Infinity, repeatDelay: 3.5, ease: 'easeOut', delay: 1.2 }}
        />
      </motion.div>
    </div>
  );
}

function SignInWithTeaser() {
  return (
    <div className="relative">
      <Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-500">
        Sign in
      </Link>
      <TeaserBubble />
    </div>
  );
}

function MobileBurgerTeaser() {
  return (
    <div className="absolute right-0 top-full z-50">
      <TeaserBubble arrowRight />
    </div>
  );
}

// External links (some require superadmin)
const externalLinks = [
  { href: '/prefect/dashboard', label: 'Prefect', superadminOnly: true },
  { href: '/api/cms/admin/', label: 'CMS', superadminOnly: true },
  { href: '/api/docs/', label: 'API Docs', superadminOnly: true },
];

export default function Layout() {
  const location = useLocation();
  const { isAuthenticated, isSuperadmin, logout, isLoading } = useAuth();
  const { exists: blogExists, loading: blogLoading } = usePageExists('blog');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const visibleExternalLinks = externalLinks.filter((link) => !link.superadminOnly || isSuperadmin);

  return (
    <div className="min-h-screen flex flex-col">
      <BoringPopup />
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-xl font-semibold text-gray-900 hover:text-gray-600 transition-colors"
              >
                bish-bash-bosh
              </Link>
              <nav className="flex gap-1">
                {!blogLoading && (
                  blogExists ? (
                    <BlogNavLink to="/blog" isActive={location.pathname.startsWith('/blog')} />
                  ) : (
                    <motion.span
                      className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-gray-400 cursor-default select-none"
                      whileHover={{ scale: 1.05 }}
                    >
                      Blog
                      <motion.span
                        className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-semibold"
                        animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        soon™
                      </motion.span>
                    </motion.span>
                  )
                )}
                {isAuthenticated && (
                  <BetsNavLink isActive={location.pathname.startsWith('/pool')} />
                )}
              </nav>
            </div>

            {/* Desktop right side */}
            <div className="hidden md:flex items-center gap-4">
              <ThemeToggle />
              {visibleExternalLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  {link.label}
                </a>
              ))}
              {!isLoading && (
                <>
                  {isAuthenticated ? (
                    <div className="flex items-center gap-3">
                      <Link to="/profile" className="text-sm text-gray-600 hover:text-gray-900">
                        Profile
                        {isSuperadmin && (
                          <span className="ml-1 text-xs text-purple-600 font-medium">(admin)</span>
                        )}
                      </Link>
                      <button
                        onClick={() => logout()}
                        className="text-sm text-gray-600 hover:text-gray-900"
                      >
                        Sign out
                      </button>
                    </div>
                  ) : (
                    <SignInWithTeaser />
                  )}
                </>
              )}
            </div>

            {/* Mobile hamburger — auth & external links only */}
            <div className="relative md:hidden">
              <button
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Toggle menu"
              >
                {menuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              {!isLoading && !isAuthenticated && !menuOpen && (
                <MobileBurgerTeaser />
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              className="md:hidden border-t border-gray-200 bg-white overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="px-4 py-3 space-y-1">
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600">
                  Theme <ThemeToggle />
                </div>
                {visibleExternalLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  >
                    {link.label}
                  </a>
                ))}
                {!isLoading && (
                  <div className="pt-2 border-t border-gray-100">
                    {isAuthenticated ? (
                      <>
                        <Link to="/profile" className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50">
                          Profile
                          {isSuperadmin && (
                            <span className="ml-1 text-xs text-purple-600 font-medium">(admin)</span>
                          )}
                        </Link>
                        <button
                          onClick={() => logout()}
                          className="block w-full text-left px-3 py-2 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        >
                          Sign out
                        </button>
                      </>
                    ) : (
                      <Link to="/login" className="block px-3 py-2 rounded-md text-sm font-medium text-blue-600 hover:text-blue-500">
                        Sign in
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center gap-4 text-sm text-gray-500">
            <span>bish-bash-bosh — a cheeky little project</span>
            <span className="text-gray-300">|</span>
            <Link to="/disclaimer" className="hover:text-gray-700">
              Disclaimer
            </Link>
            {isAuthenticated && (
              <>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setFeedbackOpen(true)}
                  className="hover:text-gray-700 transition-colors"
                >
                  Report
                </button>
              </>
            )}
          </div>
        </div>
      </footer>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}
